<template>
  <div
    class="template-media-slot"
    :style="!mediaUrls.length ? { backgroundImage: `url(/item-default.webp)` } : {}"
    :class="{ 'template-media-slot--empty': mediaUrls.length === 0, 'template-media-slot--with-action-effect': props.withActionEffect && mediaUrls.length === 0 }"
  >
    <div v-if="mediaUrls.length > 0" class="template-media-slot__view">
      <MediaView
        :model-value="mediaUrls"
        :media-type="selectedMediaType"
        :readonly="props.readonly"
        :is-slider="false"
        @update:model-value="handleUpdateMedia"
      />
    </div>
    <BaseAspectRatio
      v-else
      :width="1"
      :height="1"
      class="template-media-slot__add"
      @click="handleOpenUploader"
    >
      <BaseIcon
        name="vuesax:add-square-bulk"
        size="100px"
        color="var(--color-foreground-primary)"
        fill-type="solid"
      />
    </BaseAspectRatio>
  </div>
  <BasePopup v-model="isMediaUploaderOpen" :with-close="false">
    <MediaUploader
      v-if="isMediaUploaderOpen"
      accept-all-media-types
      :with-slider="false"
      :multiple="false"
      :max-files="1"
      :notify-invalid-files="false"
      :auto-add="true"
      height="100%"
      @update:model-value="handleUploadMedia"
    />
  </BasePopup>
</template>

<script setup lang="ts">
import { MediaUploader, MediaView } from '~/entities/media'
import { MediaType } from '~/shared/const'
import { detectMediaTypeFromUrl, isBlobUrl, isDataURL } from '~/shared/lib'
import { BaseAspectRatio, BaseIcon, BasePopup } from '~/shared/ui'

interface Props {
  modelValue?: string | string[] | null
  readonly?: boolean
  withActionEffect?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  readonly: false,
  withActionEffect: true,
})
const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
}>()

const isMediaUploaderOpen = ref(false)
const selectedMediaType = ref<MediaType>(MediaType.Image)

const mediaUrls = computed(() => {
  const val = props.modelValue
  if (Array.isArray(val)) {
    return val.filter(Boolean)
  }
  if (val) {
    return [val]
  }
  return []
})

watch(
  mediaUrls,
  (urls) => {
    const url = urls[0]

    if (!url) {
      selectedMediaType.value = MediaType.Image
      return
    }

    if (isBlobUrl(url) || isDataURL(url)) {
      return
    }

    selectedMediaType.value = detectMediaTypeFromUrl(url)
  },
  { immediate: true },
)

function handleOpenUploader() {
  if (props.readonly) {
    return
  }
  isMediaUploaderOpen.value = true
}

function handleUploadMedia(urls: string[], mediaType: MediaType) {
  isMediaUploaderOpen.value = false
  selectedMediaType.value = mediaType
  emit('update:modelValue', urls.slice(0, 1))
}

function handleUpdateMedia(urls: string[]) {
  emit('update:modelValue', urls)
}
</script>

<style lang="scss" scoped>
.template-media-slot {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &--empty {
    width: 100%;
    background-color: var(--color-surface-transparent-inverted);
    border-radius: calc(var(--radius-md) - 10px);
    padding: rem(24px);
  }

  &__view {
    width: 100%;
    height: auto;
    max-height: 100%;
    aspect-ratio: 1 / 1;
    overflow: hidden;
    border-radius: var(--radius-md-alt);

    :deep(.editor-uploader) {
      height: 100%;
    }

    :deep(.media-image) {
      height: 100%;
    }

    :deep(.media-player) {
      height: 100%;
    }
  }

  &__add {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    :deep(.base-aspect-ratio__content) {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &--with-action-effect.template-media-slot--empty {
    @include action-effect-button(var(--color-surface-transparent-inverted));
  }
}
</style>
